import { useEffect, useState } from 'react';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { authApi } from '../../store/shared/api/endpoints';
import TransparentLogo from '../../store/shared/components/TransparentLogo';
import defaultLogo from '../../store/shared/assets/logo.png';
import { ADMIN_SECTIONS, getAdminSection } from './adminSections';
import './admin.css';

export function RequireAdmin({ children }) {
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    const token = localStorage.getItem('nv_admin_token');
    if (!token) {
      setStatus('fail');
      return undefined;
    }

    setStatus('ok');

    let cancelled = false;
    authApi
      .adminMe()
      .then(() => {
        if (!cancelled) setStatus('ok');
      })
      .catch((err) => {
        if (cancelled) return;
        const code = err.response?.status;
        if (code === 401 || code === 403) {
          localStorage.removeItem('nv_admin_token');
          setStatus('fail');
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (status === 'checking') {
    return (
      <div className="admin-shell admin-shell--loading">
        <div className="admin-loader">Loading admin…</div>
      </div>
    );
  }
  if (status === 'fail') return <Navigate to="/admin" replace />;
  return children;
}

export function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const active = getAdminSection(location.hash);
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    setNavOpen(false);
  }, [location.hash]);

  const logout = () => {
    localStorage.removeItem('nv_admin_token');
    navigate('/admin', { replace: true });
  };

  return (
    <RequireAdmin>
      <div className={`admin-shell${navOpen ? ' is-nav-open' : ''}`}>
        <button
          type="button"
          className="admin-side__backdrop"
          aria-label="Close menu"
          onClick={() => setNavOpen(false)}
        />
        <aside className="admin-side" aria-label="Admin navigation">
          <div className="admin-side__top">
            <TransparentLogo src={defaultLogo} alt="NAYAVARNAM" className="admin-side__logo" />
            <p className="admin-side__tag">Admin panel</p>
          </div>
          <nav className="admin-side__nav" aria-label="Admin sections">
            {ADMIN_SECTIONS.map((item) => (
              <Link
                key={item.id}
                to={`/admin/dashboard#${item.id}`}
                className={`admin-side__link${active.id === item.id ? ' is-active' : ''}`}
                onClick={() => setNavOpen(false)}
              >
                <span className="admin-side__icon" aria-hidden>
                  {item.icon}
                </span>
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="admin-side__foot">
            <Link to="/" className="admin-side__store" onClick={() => setNavOpen(false)}>
              View storefront
            </Link>
            <button type="button" onClick={logout} className="admin-side__logout">
              Logout
            </button>
          </div>
        </aside>
        <div className="admin-content">
          <header className="admin-mobile-top">
            <button
              type="button"
              className="admin-mobile-top__menu"
              aria-label="Open menu"
              aria-expanded={navOpen}
              onClick={() => setNavOpen(true)}
            >
              <span />
              <span />
              <span />
            </button>
            <div className="admin-mobile-top__brand">
              <strong>{active.label}</strong>
              <span>Admin</span>
            </div>
          </header>
          <main className="admin-main">
            <Outlet />
          </main>
        </div>
      </div>
    </RequireAdmin>
  );
}
